import { supabase } from "../supabase";
import type {
  Notification,
  NotificationPreferences,
  NotificationType,
} from "@/types";
import { emailApi } from "./email";

const MISSING_TABLE_CODES = new Set(["42P01", "PGRST116"]);
const MISSING_TABLE_FRAGMENTS = ["does not exist", "relation", "404"];

const includesMissingFragment = (value: string): boolean => {
  const normalized = value.toLowerCase();
  return MISSING_TABLE_FRAGMENTS.some((fragment) =>
    normalized.includes(fragment),
  );
};

const safeStringify = (value: unknown): string | undefined => {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

const getErrorCode = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code?: unknown }).code === "string"
  ) {
    return (error as { code: string }).code;
  }
  return undefined;
};

const collectMessageCandidates = (error: unknown): string[] => {
  const candidates: string[] = [];

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    candidates.push((error as { message: string }).message);
  }

  if (typeof error === "string") {
    candidates.push(error);
  }

  const serialized = safeStringify(error);
  if (serialized) {
    candidates.push(serialized);
  }

  return candidates;
};

const isMissingTableError = (error: unknown): boolean => {
  const code = getErrorCode(error);
  if (code && MISSING_TABLE_CODES.has(code)) {
    return true;
  }

  return collectMessageCandidates(error).some((message) =>
    includesMissingFragment(message),
  );
};

export const notificationsApi = {
  /**
   * Získať všetky notifikácie pre používateľa
   */
  async list(options?: {
    is_read?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data?: Notification[]; error?: string }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      let query = supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (options?.is_read !== undefined) {
        query = query.eq("is_read", options.is_read);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(
          options.offset,
          options.offset + (options.limit || 50) - 1,
        );
      }

      const { data, error } = await query;

      if (error) {
        // If table doesn't exist (404/42P01), return empty array instead of error
        if (isMissingTableError(error)) {
          return { success: true, data: [] };
        }
        throw error;
      }

      return { success: true, data: data as Notification[] };
    } catch (error) {
      // Catch all errors including network errors (404)
      if (isMissingTableError(error)) {
        return { success: true, data: [] };
      }
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch notifications",
      };
    }
  },

  /**
   * Získať počet neprečítaných notifikácií
   */
  async getUnreadCount(): Promise<{
    success: boolean;
    count?: number;
    error?: string;
  }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      // Use regular SELECT instead of head: true to avoid HEAD request issues
      const { error, count } = await supabase
        .from("notifications")
        .select("id", { count: "exact" })
        .eq("user_id", user.id)
        .eq("is_read", false)
        .limit(0); // We only want count, not data

      if (error) {
        // If table doesn't exist (404/42P01), return 0 instead of error
        if (isMissingTableError(error)) {
          // Silently return 0 - table doesn't exist yet
          return { success: true, count: 0 };
        }
        throw error;
      }

      return { success: true, count: count || 0 };
    } catch (error) {
      // Catch all errors including network errors (404)
      if (isMissingTableError(error)) {
        return { success: true, count: 0 };
      }
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to get unread count",
      };
    }
  },

  /**
   * Označiť notifikáciu ako prečítanú
   */
  async markAsRead(
    notificationId: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .eq("user_id", user.id); // Bezpečnosť - len vlastné notifikácie

      if (error) {
        if (isMissingTableError(error)) {
          console.warn("Notifications table does not exist.");
          return { success: true };
        }
        throw error;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to mark as read",
      };
    }
  },

  /**
   * Označiť všetky notifikácie ako prečítané
   */
  async markAllAsRead(): Promise<{ success: boolean; error?: string }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        if (isMissingTableError(error)) {
          console.warn("Notifications table does not exist.");
          return { success: true };
        }
        throw error;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to mark all as read",
      };
    }
  },

  /**
   * Vymazať notifikáciu
   */
  async delete(
    notificationId: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId)
        .eq("user_id", user.id);

      if (error) {
        if (isMissingTableError(error)) {
          console.warn("Notifications table does not exist.");
          return { success: true };
        }
        throw error;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to delete notification",
      };
    }
  },

  /**
   * Vytvoriť notifikáciu (interné použitie)
   */
  async create(payload: {
    user_id: string;
    type: NotificationType;
    title: string;
    message: string;
    data?: Record<string, unknown>;
    sendEmail?: boolean;
  }): Promise<{ success: boolean; data?: Notification; error?: string }> {
    try {
      // Skontrolovať preferencie používateľa
      const preferences = await this.getPreferences(payload.user_id);
      const shouldSendEmail =
        payload.sendEmail && preferences.data?.email_enabled;

      const notificationData: {
        user_id: string;
        type: NotificationType;
        title: string;
        message: string;
        data?: Record<string, unknown> | null;
        email_sent: boolean;
        email_sent_at?: string;
      } = {
        user_id: payload.user_id,
        type: payload.type,
        title: payload.title,
        message: payload.message,
        data: payload.data || null,
        email_sent: false,
      };

      // Vložiť notifikáciu
      const { data: notification, error: insertError } = await supabase
        .from("notifications")
        .insert(notificationData)
        .select()
        .single();

      if (insertError) {
        // If table doesn't exist, log warning and return success (graceful degradation)
        if (isMissingTableError(insertError)) {
          console.warn(
            "Notifications table does not exist. Notification not saved. Please run the database migration 20240101000001_initial_schema.sql",
          );
          return { success: true, data: undefined };
        }
        throw insertError;
      }

      // Ak má byť odoslaný email, pošli ho
      if (
        shouldSendEmail &&
        this.shouldSendEmailForType(payload.type, preferences.data)
      ) {
        const { data: userData } = await supabase
          .from("users")
          .select("email, display_name")
          .eq("id", payload.user_id)
          .single();

        if (userData?.email) {
          try {
            await emailApi.sendEmail({
              to: userData.email,
              subject: payload.title,
              html: this.getEmailHtml(
                payload.type,
                payload.title,
                payload.message,
              ),
              user_id: payload.user_id,
            });

            // Aktualizovať email_sent
            await supabase
              .from("notifications")
              .update({
                email_sent: true,
                email_sent_at: new Date().toISOString(),
              })
              .eq("id", notification.id);
          } catch (emailError) {
            console.error("Failed to send notification email:", emailError);
            // Nezlyháva, ak sa email nepodarí odoslať
          }
        }
      }

      return { success: true, data: notification as Notification };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to create notification",
      };
    }
  },

  /**
   * Získať preferencie notifikácií
   */
  async getPreferences(userId?: string): Promise<{
    success: boolean;
    data?: NotificationPreferences;
    error?: string;
  }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const targetUserId = userId || session?.user?.id;
      if (!targetUserId) throw new Error("User not authenticated");

      // Použiť limit(1) s maybeSingle() aby sme zabránili 406 chybe
      // 406 môže vzniknúť pri neexistujúcich preferenciách alebo RLS problémoch
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", targetUserId)
        .limit(1)
        .maybeSingle();

      // Ak je 406 chyba, skúsiť alternatívny prístup - načítať ako array a vziať prvý prvok
      if (
        error &&
        (error.message?.includes("406") ||
          String(error.code || "").includes("406") ||
          error.message?.includes("Not Acceptable"))
      ) {
        console.warn("Received 406 error, trying alternative query method...");

        // Skúsiť načítať ako array namiesto single/maybeSingle
        const { data: arrayData, error: arrayError } = await supabase
          .from("notification_preferences")
          .select("*")
          .eq("user_id", targetUserId)
          .limit(1);

        if (!arrayError && arrayData && arrayData.length > 0) {
          // Našli sme dáta cez alternatívny prístup
          return {
            success: true,
            data: arrayData[0] as NotificationPreferences,
          };
        }

        // Ak stále zlyhá, vrátiť defaultné preferencie
        if (arrayError) {
          console.warn(
            "Alternative query also failed. Using default preferences.",
          );
        }

        // Vrátiť defaultné preferencie (môže to byť RLS problém)
        return {
          success: true,
          data: {
            id: 0,
            user_id: targetUserId,
            email_enabled: true,
            email_lesson_reservation: true,
            email_lesson_cancelled: true,
            email_lesson_reminder: true,
            email_payment_confirmed: true,
            email_pair_invitation: true,
            email_system_announcements: true,
            in_app_enabled: true,
            marketing_email_opt_in: false,
            marketing_email_opt_in_at: null,
            marketing_email_opt_out_at: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as NotificationPreferences,
        };
      }

      // Ak je iná chyba (nie 406)
      if (error) {
        // If table doesn't exist (404/42P01)
        if (isMissingTableError(error)) {
          console.warn(
            "Notification preferences table does not exist. Please run the database migration 20240101000001_initial_schema.sql",
          );
          // Return default preferences without saving
          return {
            success: true,
            data: {
              id: 0,
              user_id: targetUserId,
              email_enabled: true,
              email_lesson_reservation: true,
              email_lesson_cancelled: true,
              email_lesson_reminder: true,
              email_payment_confirmed: true,
              email_pair_invitation: true,
              email_system_announcements: true,
              in_app_enabled: true,
              marketing_email_opt_in: false,
              marketing_email_opt_in_at: null,
              marketing_email_opt_out_at: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as NotificationPreferences,
          };
        }
        throw error;
      }

      // Ak neexistujú preferencie (data je null), vytvor defaultné
      if (!data) {
        const defaultPrefs = {
          user_id: targetUserId,
          email_enabled: true,
          email_lesson_reservation: true,
          email_lesson_cancelled: true,
          email_lesson_reminder: true,
          email_payment_confirmed: true,
          email_pair_invitation: true,
          email_system_announcements: true,
          in_app_enabled: true,
          marketing_email_opt_in: false,
          marketing_email_opt_in_at: null,
          marketing_email_opt_out_at: null,
        };

        const { data: newPrefs, error: insertError } = await supabase
          .from("notification_preferences")
          .insert(defaultPrefs)
          .select()
          .single();

        if (insertError) throw insertError;

        return { success: true, data: newPrefs as NotificationPreferences };
      }

      return { success: true, data: data as NotificationPreferences };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to get preferences",
      };
    }
  },

  /**
   * Aktualizovať preferencie notifikácií
   */
  async updatePreferences(
    preferences: Partial<NotificationPreferences>,
  ): Promise<{
    success: boolean;
    data?: NotificationPreferences;
    error?: string;
  }> {
    try {
      // Použiť getSession() namiesto getUser() aby sme sa vyhli 403 chybám
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("notification_preferences")
        .update({ ...preferences, updated_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) {
        if (isMissingTableError(error)) {
          console.warn(
            "Notification preferences table does not exist. Preferences not saved. Please run the SQL migration from docs/notifications-schema.sql",
          );
          // Return current preferences (if available) or default
          return { success: true, data: undefined };
        }
        throw error;
      }

      return { success: true, data: data as NotificationPreferences };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update preferences",
      };
    }
  },

  /**
   * Pomocné funkcie
   */
  shouldSendEmailForType(
    type: NotificationType,
    preferences?: NotificationPreferences,
  ): boolean {
    if (!preferences?.email_enabled) return false;

    switch (type) {
      case "lesson_reservation":
        return preferences.email_lesson_reservation;
      case "lesson_cancelled":
        return preferences.email_lesson_cancelled;
      case "lesson_reminder":
        return preferences.email_lesson_reminder;
      case "payment_confirmed":
      case "package_purchased":
        return preferences.email_payment_confirmed;
      case "pair_invitation":
      case "pair_request":
        return preferences.email_pair_invitation;
      case "system_announcement":
        return preferences.email_system_announcements;
      default:
        return true;
    }
  },

  getEmailHtml(
    _type: NotificationType,
    title: string,
    message: string,
  ): string {
    // Jednoduchý HTML template pre notifikácie
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #dc2626; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9fafb; }
            .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>AC Academy</h1>
              <p>Tanečný systém</p>
            </div>
            <div class="content">
              <h2>${title}</h2>
              <p>${message}</p>
            </div>
            <div class="footer">
              <p>© 2024 AC Academy. Všetky práva vyhradené.</p>
              <p><a href="https://www.ayocruz.com" style="color: #870509; text-decoration: none;">www.ayocruz.com</a></p>
            </div>
          </div>
        </body>
      </html>
    `;
  },
};
